import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Download, X } from "lucide-react";
import { useNotification } from "../../../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../../../constants/Enums";
import DineInQrCard from "./DineInQrCard";
import { buildTableQrUrl, buildNineQrImageSettings } from "./dineInQrHelper";
import { downloadTableQrPng } from "./dineInQrDownload";
import "./styles/DineInQrModal.css";

interface DineInQrModalProps {
  businessId: string;
  businessName: string;
  businessLogoUrl?: string;
  tableNumber: number;
  onClose: () => void;
}

/** Source resolution for the downloaded PNG — not what's shown on screen. */
const DOWNLOAD_QR_SIZE = 512;

/**
 * One table's QR, blown up so a customer can scan it off the staff device
 * without leaning across the counter, plus a download for anyone who wants to
 * drop it into their own artwork.
 *
 * Portalled, same as ShareProfileModal — this renders from inside the business
 * page's tab content, which sits under a sticky tab bar and its own stacking
 * context, so an in-place overlay would end up trapped beneath it.
 */
const DineInQrModal: React.FC<DineInQrModalProps> = ({
  businessId,
  businessName,
  businessLogoUrl,
  tableNumber,
  onClose,
}) => {
  const { triggerNotification } = useNotification();
  const downloadQrRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const handleDownload = async () => {
    const qrCanvas = downloadQrRef.current?.querySelector("canvas");
    if (!qrCanvas) {
      triggerNotification("Couldn't prepare the QR code.", NotificationType.ERROR);
      return;
    }

    setSaving(true);
    try {
      const ok = await downloadTableQrPng({
        qrCanvas,
        businessName,
        businessLogoUrl,
        tableNumber,
      });
      if (!ok) {
        triggerNotification("Couldn't save the QR code.", NotificationType.ERROR);
      }
    } catch {
      // Most likely a tainted canvas despite the CORS request — the download
      // fails, the modal doesn't.
      triggerNotification("Couldn't save the QR code.", NotificationType.ERROR);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="dine-in-qr-modal__overlay" onClick={onClose} role="presentation">
      <div
        className="dine-in-qr-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`QR code for table ${tableNumber}`}
      >
        <button
          type="button"
          className="dine-in-qr-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Off-screen high-resolution source for the download. The visible QR
            is sized for the screen; exporting that would produce a file too
            soft to print or hand to a designer. Kept in the DOM rather than
            generated on demand so the canvas is guaranteed painted by the time
            the button is pressed. */}
        <div ref={downloadQrRef} className="dine-in-qr-modal__source" aria-hidden="true">
          <QRCodeCanvas
            value={buildTableQrUrl(businessId, tableNumber)}
            size={DOWNLOAD_QR_SIZE}
            level="H"
            includeMargin={false}
            imageSettings={buildNineQrImageSettings(DOWNLOAD_QR_SIZE)}
          />
        </div>

        <DineInQrCard
          businessId={businessId}
          businessName={businessName}
          businessLogoUrl={businessLogoUrl}
          tableNumber={tableNumber}
          size={228}
        />

        <button
          type="button"
          className="dine-in-qr-modal__download"
          onClick={handleDownload}
          disabled={saving}
        >
          <Download size={16} strokeWidth={2} />
          {saving ? "Saving..." : "Download PNG"}
        </button>

        {/* The literal URL, for the case the camera won't cooperate — someone
            can always type it. Selectable text, not a button. */}
        <p className="dine-in-qr-modal__url">{buildTableQrUrl(businessId, tableNumber)}</p>
      </div>
    </div>,
    document.body
  );
};

export default DineInQrModal;
